import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import axiosPrivate from 'config/axiosPrivate';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { animationUrl } from 'config/endpoints';
import { useHistory, useParams } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';
import { CustomAutocomplete } from '../../components/shared/DropDown/CustomAutocomplete';

export default function CreateCustomAIAgentFormTranslation({
                                                             returnToCreateProject,
                                                             selectedWebsite,
                                                             selectedLanguage, // <<< langue "globale" passée par le parent (cachée dans le formulaire)
                                                           }) {
  const { agentId } = useParams();
  const history = useHistory();

  const [fileName, setFileName] = useState('Aucun fichier sélectionné');
  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [availableLanguagesList, setAvailableLanguagesList] = useState([]);
  const { t } = useTranslation();

  const API = {
    CreateAgentAI: '/smart-content/agent_ia/traduction/create',
    GetAgentAI: (id) => `/smart-content/agent_ia/${id}`,
    UpdateAgentAI: (id) => `/smart-content/agent_ia/traduction/${id}/update`,
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setKnowledgeBaseFiles(files);
    setFileName(files.map((f) => f.name).join(', '));
  };

  // État local
  const [formData, setFormData] = useState({
    nomAgent: '',
    objectif: '',
    instructions: '',
    tonalite: '',
    cible: '',
    structureText: '',
    RedacRules: '',
    structureTexte: '',
    InterditExpressions: '',
    // 1) langue "source" / globale (VENANT DU PARENT) — non affichée, envoyée sous 'langue'
    langue: selectedLanguage.language,
    langueTraduction: null,
    TypeProducts: '',
    recommandedLexique: '',
    conaissances: '',
    urls: '',
    selectedWebsite: selectedWebsite,
    websiteId: selectedWebsite?.id || '',
  });

  const isEditMode = Boolean(agentId);

  // Charger la liste des langues pour l'UI de la langue de traduction
  useEffect(() => {
    axiosPrivate
        .get('/project/language/all')
        .then((res) => setAvailableLanguagesList(res.data || []))
        .catch(() => setAvailableLanguagesList([]));
  }, []);

  // Sync site si la prop change
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      selectedWebsite,
      websiteId: selectedWebsite?.id || '',
    }));
  }, [selectedWebsite]);

  // Sync langue parent si la prop change (sans l'afficher)
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      langue: selectedLanguage.language,
    }));
  }, [selectedLanguage]);
  useEffect(() => {
    document.title = "Typix - Agents IA";
  }, []);

  // Mode édition : chargement + hydratation (langue + langueTraduction si renvoyées)
  useEffect(() => {
    if (!isEditMode) return;
    axiosPrivate
        .get(API.GetAgentAI(agentId))
        .then((res) => {
          const data = res.data || {};
          setFormData((prev) => ({
            ...prev,
            nomAgent: data.name || '',
            objectif: data.role || '',
            instructions: data.instructions || '',
            tonalite: data.tonality || '',
            cible: data.audience || '',
            structureTexte: data.resultStructure || '',
            RedacRules: data.redactionRules || '',
            recommandedLexique: data.recommendedLexicons || '',
            InterditExpressions: data.bannedExpressions || '',
            TypeProducts: data.productType || '',
            websiteId: data.websiteId || '',
            conaissances: Array.isArray(data.knowledgeBase) ? data.knowledgeBase.join(', ') : '',
            urls: Array.isArray(data.urls) ? data.urls.join(', ') : '',
            // garde la 'langue' (parent) sauf si le back en renvoie une
            langue: data.langue || prev.langue || '',
            // si le back renvoie une langue de traduction, on essaie de la remapper
            langueTraduction: (() => {
              if (!data.langueTraduction) return prev.langueTraduction || null;
              // On tente de faire matcher avec la liste disponible (id, code, libellé)
              const match =
                  availableLanguagesList.find(
                      (l) =>
                          l.id === data.langueTraduction?.id ||
                          l.id === data.langueTraduction ||
                          l.language === data.langueTraduction?.language ||
                          l.language === data.langueTraduction
                  ) || data.langueTraduction;
              return match;
            })(),
          }));
        })
        .catch(() => {});
  }, [agentId, isEditMode, availableLanguagesList]);

  const handleSubmit = () => {
    const formDataFORM = new FormData();

    // Champs de base
    formDataFORM.append('name', formData.nomAgent || '');
    formDataFORM.append('instructions', formData.instructions || '');

    // Website
    const websiteIdToSend = formData?.selectedWebsite?.id || formData?.websiteId || '';
    if (websiteIdToSend) {
      formDataFORM.append('websiteId', websiteIdToSend);
    }

    // 1) Langue (depuis le parent) — obligatoire selon tes contraintes
    const langueToSend = formData?.langue || '';
    if (!langueToSend) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('La langue est requise'));
      setErrorSuccess(true);
      return;
    }
    formDataFORM.append(t('langue'), langueToSend);

    // 2) Langue de traduction (sélectionnée dans l’UI) — requise (champ avec *)
    const lt = formData?.langueTraduction;
    const langueTradToSend =
        (lt && (lt.id || lt.code || lt.language)) || (typeof lt === 'string' ? lt : '');
    if (!langueTradToSend) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('La langue de traduction est requise'));
      setErrorSuccess(true);
      return;
    }
    formDataFORM.append(t('langueTraduction'), langueTradToSend);

    // Fichiers
    knowledgeBaseFiles.forEach((file) => {
      formDataFORM.append('knowledgeBaseFiles', file);
    });

    // URLs (tableau)
    const urlsArray =
        formData.urls && formData.urls.trim()
            ? formData.urls.split(',').map((item) => item.trim()).filter(Boolean)
            : [];
    formDataFORM.append('urls', urlsArray);

    const request = isEditMode
        ? axiosPrivate.put(API.UpdateAgentAI(agentId), formDataFORM, {
          headers: { 'X-OpenAI-Api-Key': '', 'Content-Type': 'multipart/form-data' },
        })
        : axiosPrivate.post(API.CreateAgentAI, formDataFORM, {
          headers: { 'X-OpenAI-Api-Key': '', 'Content-Type': 'multipart/form-data' },
        });

    request
        .then(() => {
          setIsSuccess(true);
          setErrorSuccessMsg(
              `${t("L'agent")} ${formData.nomAgent}  ${
                  isEditMode ? t('a été modifié avec succès') : t('a été créé avec succès')
              }!`
          );
          setErrorSuccessImg(animationUrl + '/01_CommandeCrée.gif');
          setErrorSuccess(true);
        })
        .catch((e) => {
          setIsSuccess(false);
          switch (e?.response?.status) {
            case 409:
              setErrorSuccessMsg(t("L'agent déjà existant, veuillez modifier les informations"));
              break;
            default:
              setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
              break;
          }
          setErrorSuccess(true);
        });
  };

  const handleCancel = () => {
    // Reset mais on conserve site + langue parent
    setFormData({
      nomAgent: '',
      objectif: '',
      instructions: '',
      tonalite: '',
      cible: '',
      structureText: '',
      RedacRules: '',
      structureTexte: '',
      InterditExpressions: '',
      recommandedLexique: '',
      TypeProducts: '',
      conaissances: '',
      urls: '',
      selectedWebsite,
      websiteId: selectedWebsite?.id || '',
      langue: selectedLanguage.language,
      langueTraduction: null,
    });
    if (typeof returnToCreateProject === 'function') {
      returnToCreateProject();
    } else {
      history.goBack();
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const containerClass = !returnToCreateProject ? 'box-wrapper p-4' : 'p-4';

  return (
      <div className={containerClass}>
        <div className="box-header-top">
          <span onClick={handleCancel} className="bt-circle me-3" style={{ cursor: 'pointer' }} />
          <h2 className="mt-1">
            {isEditMode
                ? t('Éditer un agent IA personnalisé')
                : t('Créer un agent IA personnalisé')}
          </h2>
        </div>

        <div className="row row-mb">
          {/* Colonne gauche */}
          <div className="col-12 col-md-6">
            <div className="mb-3">
              <div className="label-title">{t("Nom de l’agent*")}</div>
              <input
                  type="text"
                  className="form-control form-control-valid form-ct"
                  placeholder={t("Nom de l’agent...")}
                  value={formData.nomAgent}
                  onChange={(e) => handleChange('nomAgent', e.target.value)}
              />
            </div>

            {/* Langue (du parent) est cachée — on n’affiche que la langue de traduction */}
            <div className="mb-3">
              <div className="label-title">{t('Langue de traduction*')}</div>
              <CustomAutocomplete
                  disabled={availableLanguagesList.length <= 0}
                  list={availableLanguagesList}
                  value={formData.langueTraduction}
                  setValue={(option) => handleChange('langueTraduction', option)}
                  groupBy={() => false}
                  getOptionSelected={(option) => option?.id === formData.langueTraduction?.id}
                  getOptionDisabled={() => false}
                  getOptionLabel={(option) => (option?.language ? t(option.language) : '')}
              />
            </div>

            <div className="mb-3">
              <div className="label-title">
                {t('Instructions de personnalisation')}{' '}
                <span id="instructionredactooltip" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>
                ⓘ
              </span>
              </div>
              <textarea
                  className="form-control form-control-valid form-ct"
                  placeholder={t('Ex: ne traduis pas de façon littérale.')}
                  value={formData.instructions}
                  onChange={(e) => handleChange('instructions', e.target.value)}
                  maxLength={500}
              />
              <UncontrolledTooltip placement="right" target="instructionredactooltip" className="custom-redac-tooltip">
                {t("Ajouter des éléments que l'IA doit ou ne pas faire ou mentionner")}
              </UncontrolledTooltip>
            </div>
          </div>

          {/* Colonne droite */}
          <div className="col-12 col-md-6">
            <div className="mb-3">
              <div className="label-title">{t('Base de connaissance locale')}</div>
              <div
                  className="upload-file__controls form-control form-control-valid form-ct d-flex align-items-center w-100"
                  style={{ padding: '0', borderRadius: '6px' }}
              >
                <input type="file" id="upload-file__input" multiple style={{ display: 'none' }} onChange={handleFileChange} />
                <label
                    htmlFor="upload-file__input"
                    className="upload-file__browse-btn"
                    style={{
                      backgroundColor: '#ffe426',
                      border: 'none',
                      color: '#000',
                      borderTopLeftRadius: '6px',
                      borderBottomLeftRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontWeight: 350,
                      height: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                >
                  Sélect. fichiers
                </label>
                <span className="upload-file__status" style={{ color: '#6c757d', paddingLeft: '12px' }}>
                {fileName}
              </span>
              </div>
            </div>

            <div className="mb-3">
              <div className="label-title">
                {t('Base de connaissance en ligne')}{' '}
                <span id="connaissancetrad" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>
                ⓘ
              </span>
              </div>
              <textarea
                  className="form-control form-control-valid form-ct"
                  placeholder={t('Ex: URL1, URL2')}
                  value={formData.urls}
                  onChange={(e) => handleChange('urls', e.target.value)}
                  maxLength={500}
              />
              <UncontrolledTooltip placement="right" target="connaissancetrad" className="custom-redac-tooltip">
                {t("Indiquer la liste des urls sur lesquels l'IA peut s'appuyer")}
              </UncontrolledTooltip>
            </div>
          </div>

          <div className="box-button-bt d-flex align-items-center justify-content-center mt-4">
            <div className="me-4">
              <button type="button" className="bt-cancel me-3" onClick={handleCancel}>
                Annuler
              </button>
            </div>
            <div>
              <button type="button" onClick={handleSubmit} className="bt-submit-y">
                Valider
              </button>
            </div>
          </div>
        </div>

        <ErrorSuccesModal
            open={errorSuccess}
            message={errorSuccessMsg}
            handleClose={() => {
              if (isSuccess && isEditMode) {
                history.goBack();
              } else if (isSuccess) {
                handleCancel();
              } else {
                setErrorSuccess(false);
              }
            }}
            success={isSuccess}
            toggle={() => setErrorSuccess(!errorSuccess)}
            imageUrl={errorSuccessImg}
        />
      </div>
  );
}
